import React from 'react';

import { Scan, ScanningRole } from '@bringg/types';
import CopyToClipboardTooltip from 'components/copy-to-clipboard-tooltip/copy-to-clipboard-tooltip';
import DateDisplay from 'components/date-display/date-display';
import LocationIcon from 'components/location-icon/location-icon';
import { MapData } from 'components/mapbox/mapbox';
import { validateLocation } from '../../../services/validation-rules';
import { MarkerData } from 'types/maps.consts';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { Tooltip } from '../../index';

import './attachment-scans.scss';

export interface Translations {
	scans_per_stop: string;
	driver: string;
	dispatcher: string;
	manual_scan: string;
	automatic_scan: string;
	default_copy_tooltip: string;
	success_copy_tooltip: string;
}

interface AttachmentScansProps {
	scans: Scan[];
	translations: Translations;
	dateFormat: string;
	mapData?: MapData;
	timezone?: string;
	setMapMarker: (marker: MarkerData) => void;
}

const AttachmentScans: React.FC<AttachmentScansProps> = ({
	scans,
	translations,
	dateFormat,
	mapData,
	timezone,
	setMapMarker
}) => {
	const ScanningAuthorNameOrRoleUser: React.FC<{
		authorName: string;
		scanningRole: ScanningRole;
		translations: Translations;
	}> = ({ authorName, scanningRole, translations }) => {
		const getScanningAuthorNameOrRoleText = (): string => {
			if (authorName) {
				return authorName;
			}

			switch (scanningRole) {
				case ScanningRole.DRIVER: {
					return translations.driver;
				}

				case ScanningRole.DISPATCHER_COC: {
					return translations.dispatcher;
				}

				case ScanningRole.DISPATCHER_STAGING: {
					return translations.dispatcher;
				}
			}
		};

		return <span className="scanning-author-name-or-role-text">{getScanningAuthorNameOrRoleText()}</span>;
	};

	const GetScanIconAndTooltip: React.FC<{ scanString: string; manualScan: boolean }> = ({
		scanString,
		manualScan
	}) => {
		return (
			<div className="scanning-details">
				<div className="icon-scan-string">
					{manualScan ? (
						<Tooltip title={translations.manual_scan}>
							<BringgIcon
								iconName={BringgFontIcons.Keyboard}
								data-testid="keyboard-icon"
								data-test-id="keyboard-icon"
							/>
						</Tooltip>
					) : (
						<Tooltip title={translations.automatic_scan}>
							<BringgIcon
								iconName={BringgFontIcons.Scanner}
								data-testid="scanner-icon"
								data-test-id="scanner-icon"
							/>
						</Tooltip>
					)}
					<span className="scan-string">{scanString}</span>
				</div>
				<CopyToClipboardTooltip
					className="copy-scan-string"
					text={scanString}
					translations={{
						defaultTooltip: translations.default_copy_tooltip,
						successTooltip: translations.success_copy_tooltip
					}}
					padding={0}
					fontSize={12}
				/>
			</div>
		);
	};

	return (
		<div className="task-scans-attachment">
			<div>
				<div className="task-scan-title">{translations.scans_per_stop + ` (${scans.length})`}</div>
				<div className="task-scans-list">
					{scans.map((scan: Scan) => (
						<div className="task-scan-attachment" key={scan.id} data-test-id={scan.id}>
							<div className="title-and-location">
								<ScanningAuthorNameOrRoleUser
									authorName={scan.author_name}
									scanningRole={scan.scanning_role}
									translations={translations}
								/>
								{mapData && validateLocation(scan.lng, scan.lat) && (
									<LocationIcon setMapMarker={setMapMarker} lat={scan.lat} lng={scan.lng} />
								)}
							</div>
							<div className="scan-date">
								<DateDisplay date={scan.created_at} format={dateFormat} timezone={timezone} />
							</div>
							<GetScanIconAndTooltip scanString={scan.scan_string} manualScan={scan.manual_scan} />
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

export default AttachmentScans;
